<script setup>
import { defineProps, defineEmits } from 'vue'

const props = defineProps({
  node: { type: Object, required: true },
  depth: { type: Number, default: 0 },
  selectedNodeId: { type: String, default: null },
  renameNodeId: { type: String, default: null },
  renameValue: { type: String, default: '' },
  newNodeInput: { type: Object, default: null }
})

const emit = defineEmits([
  'node-click',
  'context-menu',
  'rename-update',
  'rename-confirm',
  'rename-cancel',
  'new-node-update',
  'new-node-confirm',
  'new-node-cancel'
])
</script>

<template>
  <div class="tree-node-wrapper">
    <div
      class="tree-node"
      :class="{
        'is-selected': selectedNodeId === node.node_id,
        'is-folder': node.type === 'folder',
        'is-document': node.type === 'document'
      }"
      :style="{ paddingLeft: (depth * 16 + 10) + 'px' }"
      @click="emit('node-click', node)"
      @contextmenu.prevent="emit('context-menu', $event, node)"
    >
      <!-- 重命名模式 -->
      <span v-if="renameNodeId === node.node_id" class="rename-wrap">
        <input
          :value="renameValue"
          class="rename-input"
          @input="emit('rename-update', $event.target.value)"
          @keyup.enter="emit('rename-confirm')"
          @keyup.escape="emit('rename-cancel')"
          @blur="emit('rename-confirm')"
          autofocus
        />
      </span>
      <template v-else>
        <span class="node-toggle" v-if="node.type === 'folder'">
          {{ node.isExpanded ? '▾' : '▸' }}
        </span>
        <span class="node-icon">
          <template v-if="node.type === 'folder'">
            {{ node.isExpanded ? '📂' : '📁' }}
          </template>
          <template v-else>📄</template>
        </span>
        <span class="node-name">{{ node.name }}</span>
      </template>
    </div>

    <!-- 子节点递归 -->
    <template v-if="node.type === 'folder' && node.isExpanded && node.children?.length">
      <TreeNode
        v-for="child in node.children"
        :key="child.node_id"
        :node="child"
        :depth="depth + 1"
        :selected-node-id="selectedNodeId"
        :rename-node-id="renameNodeId"
        :rename-value="renameValue"
        :new-node-input="newNodeInput"
        @node-click="(n) => emit('node-click', n)"
        @context-menu="(e, n) => emit('context-menu', e, n)"
        @rename-update="(v) => emit('rename-update', v)"
        @rename-confirm="emit('rename-confirm')"
        @rename-cancel="emit('rename-cancel')"
        @new-node-update="(v) => emit('new-node-update', v)"
        @new-node-confirm="emit('new-node-confirm')"
        @new-node-cancel="emit('new-node-cancel')"
      />
    </template>

    <!-- 当前文件夹下的新建输入框 -->
    <div
      v-if="newNodeInput && newNodeInput.visible && newNodeInput.parentId === node.node_id && node.isExpanded"
      class="new-node-input"
      :style="{ paddingLeft: ((depth + 1) * 16 + 10) + 'px' }"
    >
      <span class="node-icon">{{ newNodeInput.type === 'folder' ? '📁' : '📄' }}</span>
      <input
        :value="newNodeInput.value"
        :placeholder="newNodeInput.type === 'folder' ? '文件夹名称' : '文档名称'"
        class="rename-input"
        @input="emit('new-node-update', $event.target.value)"
        @keyup.enter="emit('new-node-confirm')"
        @keyup.escape="emit('new-node-cancel')"
        @blur="emit('new-node-confirm')"
        autofocus
      />
    </div>
  </div>
</template>

<style scoped>
.tree-node {
  display: flex;
  align-items: center;
  padding: 4px 10px;
  cursor: pointer;
  border-radius: 4px;
  margin: 1px 4px;
  transition: background 0.12s;
  user-select: none;
}

.tree-node:hover {
  background: #f3f4f6;
}

.tree-node.is-selected {
  background: #e0f2fe;
  color: #0284c7;
}

.node-toggle {
  width: 14px;
  font-size: 12px;
  color: #9ca3af;
  flex-shrink: 0;
  text-align: center;
}

.node-icon {
  margin-right: 6px;
  font-size: 14px;
  flex-shrink: 0;
}

.node-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rename-wrap {
  flex: 1;
}

.rename-input {
  width: 100%;
  border: 1px solid #0ea5e9;
  border-radius: 3px;
  padding: 2px 6px;
  font-size: 13px;
  outline: none;
}

.rename-input:focus {
  box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.15);
}

.new-node-input {
  display: flex;
  align-items: center;
  padding: 4px 10px;
  margin: 1px 4px;
}
</style>
